/**
 * StallDiagnostics — shared stall-honesty banner.
 *
 * Surfaces the orchestrator job's `attempt_count` and `last_error` so a
 * stalled analysis is self-diagnosable. Four cases:
 *   - Terminal failure/cancellation (isTerminal + analysis.error set + phase
 *     'failed' | 'cancelled') → red banner.
 *   - Terminal partial completion (isTerminal + partial_completion_reason set)
 *     → amber "stopped early" panel. Spec 209: the orchestrator can end a run
 *     early (no-progress guard, elapsed-time guard, spending limit) AFTER it has
 *     already produced usable recommendations. That run completes successfully,
 *     so it must NOT claim "Analysis failed" — but the reason must still be
 *     shown, or the truncation is invisible.
 *   - Non-terminal with retries / errors → amber diagnostic block.
 *   - Non-terminal but quiet → small slate info card so users still see
 *     the next-eligible timestamp + a troubleshooting pointer.
 *
 * Extracted from the Coverage detail page (spec 087) so the Coverage
 * dashboard can reuse the exact same honesty treatment. Markup + testids
 * (`stall-diagnostics-failed` / `-partial` / `-warn` / `-info`) are preserved
 * verbatim.
 *
 * The `analysis` prop is intentionally the minimal slice both endpoints
 * return — `{ phase, error, partial_completion_reason, job }` — so callers map
 * their own response shape onto it without dragging the full detail interface
 * along.
 */
import { Link } from 'react-router';
import { Icon } from './ui/Icon.js';
import { isCoverageStalled } from '../lib/coverageStaleness.js';

export interface StallDiagnosticsJob {
  attempt_count: number;
  last_error: string | null;
  locked_until: string;
  updated_at: string;
}

export interface StallDiagnosticsAnalysis {
  phase: string;
  error: string | null;
  /**
   * Spec 209 — plain-language reason an otherwise-successful run stopped before
   * covering every topic cluster. Mutually exclusive with `error`: a partially
   * completed run keeps `phase='completed'` and a NULL `error`.
   */
  partial_completion_reason: string | null;
  job: StallDiagnosticsJob | null;
}

export interface StallDiagnosticsProps {
  analysis: StallDiagnosticsAnalysis;
  isTerminal: boolean;
}

export function StallDiagnostics({ analysis, isTerminal }: StallDiagnosticsProps) {
  const job = analysis.job;
  const hasAttempts = (job?.attempt_count ?? 0) > 0;
  const lastError = job?.last_error ?? analysis.error ?? null;
  const showAmber = !isTerminal && (hasAttempts || lastError !== null);
  /* Spec 209 — the red "Analysis failed" banner is reserved for runs that
     genuinely failed or were cancelled. A run that completed with partial
     coverage carries its reason in `partial_completion_reason`, never `error`. */
  const showRed =
    isTerminal &&
    Boolean(analysis.error) &&
    (analysis.phase === 'failed' || analysis.phase === 'cancelled');
  const showPartial = isTerminal && Boolean(analysis.partial_completion_reason);
  const showSlate =
    !showAmber && !showRed && !isTerminal && job !== null;

  if (showRed) {
    return (
      <div
        data-testid="stall-diagnostics-failed"
        className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800"
      >
        <p className="font-semibold">Analysis failed</p>
        <p className="mt-1">
          <strong>Error:</strong> {analysis.error}
        </p>
        {job && job.attempt_count > 0 && (
          <p className="mt-1 text-xs text-rose-700">
            Tried {job.attempt_count} time{job.attempt_count === 1 ? '' : 's'}{' '}
            before failing.
          </p>
        )}
      </div>
    );
  }

  if (showPartial) {
    return (
      <div
        data-testid="stall-diagnostics-partial"
        className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
      >
        <p className="font-semibold flex items-center gap-1.5">
          <Icon name="bx-error" /> Analysis stopped early
        </p>
        <p className="mt-2">{analysis.partial_completion_reason}</p>
        <p className="mt-2 text-xs text-amber-700">
          The results below are everything that was produced before the analysis
          stopped. They are complete and usable as far as they go, but they do
          not cover the whole site.
        </p>
      </div>
    );
  }

  if (showAmber) {
    return (
      <div
        data-testid="stall-diagnostics-warn"
        className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
      >
        <p className="font-semibold flex items-center gap-1.5">
          <Icon name="bx-error" /> Phase is retrying
        </p>
        <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
          <dt className="text-amber-700">Current phase</dt>
          <dd className="font-mono">{analysis.phase}</dd>
          {hasAttempts && (
            <>
              <dt className="text-amber-700">Attempts</dt>
              <dd>{job!.attempt_count}</dd>
            </>
          )}
          {job && (
            <>
              <dt className="text-amber-700">Last touched</dt>
              <dd>{new Date(job.updated_at).toLocaleString()}</dd>
              <dt className="text-amber-700">Next eligible</dt>
              <dd>{new Date(job.locked_until).toLocaleString()}</dd>
            </>
          )}
        </dl>
        {lastError && (
          <details className="mt-2">
            <summary className="cursor-pointer text-xs font-semibold text-amber-800 select-none">
              Last error
            </summary>
            <pre className="mt-1 whitespace-pre-wrap break-words rounded-md bg-amber-100 p-2 text-xs text-amber-900">
              {lastError}
            </pre>
          </details>
        )}
        <p className="mt-2 text-xs text-amber-700">
          Phase handlers retry every ~5 minutes after a transient failure. If
          attempts keep climbing, check the relevant{' '}
          <Link to="/admin/feature-flags" className="underline hover:text-amber-900">
            feature flag
          </Link>{' '}
          and recent server logs.
        </p>
      </div>
    );
  }

  if (showSlate) {
    const lockedUntilDate = new Date(job!.locked_until);
    const isEligibleNow = lockedUntilDate.getTime() < Date.now();
    /* Spec 149 — only escalate ("cron may not be firing") when the job has been
       genuinely quiet past the stale window. A job whose lock simply expired
       between ticks is healthy: it's eligible now and will be picked up on the
       next cron tick, NOT a stall. */
    const isStalled = isCoverageStalled(analysis.phase, job!.updated_at);
    return (
      <div
        data-testid="stall-diagnostics-info"
        className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600"
      >
        <p>
          <span className="font-semibold text-slate-700">Phase:</span>{' '}
          <span className="font-mono">{analysis.phase}</span>
          <span className="mx-2 text-slate-300">·</span>
          <span className="text-slate-500">
            Next cron tick eligible{' '}
            {isStalled ? (
              <span className="text-amber-700">now (overdue)</span>
            ) : isEligibleNow ? (
              <>now</>
            ) : (
              <>at {lockedUntilDate.toLocaleString()}</>
            )}
          </span>
        </p>
        {isStalled ? (
          <p className="mt-1 text-amber-700">
            This phase hasn't advanced for a while. The cron worker may not be
            firing — check{' '}
            <Link to="/admin/feature-flags" className="underline">
              flag.worker.sitemap_analysis
            </Link>{' '}
            and the orchestrator logs.
          </p>
        ) : (
          <p className="mt-1 text-slate-500">
            Analysis in progress — the orchestrator advances this on each cron
            tick.
          </p>
        )}
      </div>
    );
  }

  return null;
}
